import { atom } from 'jotai'
import type { AuthState, AuthStatus } from '../types/Auth.types'
import { UserService } from '@duck/api-definitions'

// Base atoms for user data
export const userAtom = atom<UserService.User | null>(null)
export const accessTokenAtom = atom<string | null>(null)
export const organizationTypeAtom = atom<UserService.OrganizationType | null>(null)
export const isAuthenticatedAtom = atom<boolean>(false)
export const authStatusAtom = atom<AuthStatus>('unauthenticated')
export const authErrorAtom = atom<string | null>(null)
export const authErrorCodeAtom = atom<string | null>(null)
export const authInitializingAtom = atom<boolean>(true)

// UI state atoms
export const isLoginDialogOpenAtom = atom<boolean>(false)
export const isSignupDialogOpenAtom = atom<boolean>(false)
export const isYouHaveAssignmentsDialogOpenAtom = atom<boolean>(false)
export const youHaveAssignmentsOrganizationNameAtom = atom<string | null>(null)

// Invitation state atoms
export const invitationDataAtom = atom<UserService.InvitationDetails | null>(null)
export const invitationLoadingAtom = atom<boolean>(false)
export const assignmentTokenAtom = atom<string | null>(null)

// Derived atom that combines all auth state
export const authStateAtom = atom<AuthState>((get) => ({
  user: get(userAtom),
  isAuthenticated: get(isAuthenticatedAtom),
  error: get(authErrorAtom)
}))

// Write-only atoms for auth actions
export const setUserAtom = atom(
  null,
  (get, set, user: UserService.User | null, organizationType?: UserService.OrganizationType | null) => {
    set(userAtom, user)
    set(isAuthenticatedAtom, !!user)
    set(authStatusAtom, user ? 'authenticated' : 'unauthenticated')
    // When user is cleared (logout), clear organizationType. When user is set but organizationType
    // is omitted (e.g. setUser(updatedUser) from settings/language change), preserve current value.
    const nextOrgType =
      user === null ? null : (organizationType !== undefined ? organizationType : get(organizationTypeAtom))
    set(organizationTypeAtom, nextOrgType)
    if (user) {
      set(authErrorAtom, null)
      set(authErrorCodeAtom, null)
    }
  }
)


export const setAuthErrorAtom = atom(
  null,
  (get, set, errorData: { error: string | null; errorCode?: string | null } | string | null) => {
    let nextError: string | null

    if (typeof errorData === 'string' || errorData === null) {
      // Legacy support for plain string errors
      nextError = errorData
      set(authErrorAtom, errorData)
      set(authErrorCodeAtom, null)
    } else {
      // New support for error with errorCode
      nextError = errorData?.error ?? null
      set(authErrorAtom, nextError)
      set(authErrorCodeAtom, errorData?.errorCode || null)
    }

    if (nextError) {
      set(authStatusAtom, 'error')
    } else {
      const hasUser = !!get(userAtom)
      set(authStatusAtom, hasUser ? 'authenticated' : 'unauthenticated')
    }
  }
)

export const logoutAtom = atom(
  null,
  (get, set) => {
    set(userAtom, null)
    set(authErrorAtom, null)
    set(accessTokenAtom, null)
    set(authErrorCodeAtom, null)
    set(invitationDataAtom, null)
    set(assignmentTokenAtom, null)
    set(isAuthenticatedAtom, false)
    set(organizationTypeAtom, null)
    set(invitationLoadingAtom, false)
    set(authStatusAtom, 'unauthenticated')
    set(isYouHaveAssignmentsDialogOpenAtom, false)
    set(youHaveAssignmentsOrganizationNameAtom, null)
  }
)

// Computed atoms for user display
export const userDisplayNameAtom = atom<string>((get) => {
  const user = get(userAtom)
  if (!user) return 'Guest'
  
  const fullName = `${user.firstName} ${user.lastName}`.trim()
  return fullName || user.email.split('@')[0] || 'User'
})

export const userInitialsAtom = atom<string>((get) => {
  const user = get(userAtom)
  if (!user) return 'G'
  
  const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || ''
  const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || ''
  
  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`
  }
  
  return user.email?.charAt(0)?.toUpperCase() || 'U'
}) 